import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton,
  Typography, Box, CircularProgress, Divider, Table,
  TableBody, TableCell, TableContainer, TableRow, Paper,
  Grid, TextField, Button, TableHead, Chip, Avatar,
  useTheme, useMediaQuery, Card, CardContent, Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterListIcon,
  SwapHoriz as SwapHorizIcon,
  CurrencyExchange as CurrencyExchangeIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getTransactionHistoriesByPhoneAndDate } from '../../redux/slices/creditSlice' // adjust import

const TransactionHistoryModal = ({ open, handleClose, currentUserPhone }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const trx_history_loading = useSelector((state) => state.credits.credit_history_loading);
  const trx_history_data = useSelector((state) => state.credits.credit_history_data);
  const trx_history_message = useSelector((state) => state.credits.credit_history_message);

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const formattedToday = `${yyyy}-${mm}-${dd}`;

  const [from, setFrom] = useState(formattedToday);
  const [to, setTo] = useState(formattedToday);

  const fetchHistory = () => {
    dispatch(getTransactionHistoriesByPhoneAndDate({
      phone: currentUserPhone,
      from,
      to
    }));
  };

  useEffect(() => {
    if (open && currentUserPhone) {
      fetchHistory();
    }
  }, [open, currentUserPhone]);

  const calculateSummary = () => {
    let inCredit = 0, outCredit = 0, inBirr = 0, outBirr = 0;

    trx_history_data?.forEach(tx => {
      const isIncoming = tx.receiverPhone === currentUserPhone;
      if (isIncoming) {
        inCredit += tx.amountTransferred;
        inBirr += tx.creditInBirr;
      } else {
        outCredit += tx.amountTransferred;
        outBirr += tx.creditInBirr;
      }
    });

    return { inCredit:inCredit.toFixed(2), outCredit:outCredit.toFixed(2), inBirr:inBirr.toFixed(2), outBirr:outBirr.toFixed(2) };
  };

  const summary = trx_history_data ? calculateSummary() : null;

  const isIncomingTransaction = (tx) => tx.receiverPhone === currentUserPhone;

  const getTransactionTypeChip = (tx) => {
    const isIncoming = isIncomingTransaction(tx);
    return (
      <Chip
        icon={isIncoming ? <TrendingUpIcon /> : <TrendingDownIcon />}
        label={isIncoming ? 'Incoming' : 'Outgoing'}
        color={isIncoming ? 'success' : 'warning'}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth={isMobile ? false : "lg"}
      fullScreen={isMobile}
      PaperProps={{
        sx: isMobile ? { margin: 0, maxHeight: '100vh' } : {}
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 }
        }}
      >
        <SwapHorizIcon />
        <Typography 
          variant={isSmallMobile ? "subtitle1" : "h6"} 
          component="div" 
          sx={{ flexGrow: 1 }}
        >
          {isSmallMobile ? `History - ${currentUserPhone}` : `Transaction History For ${currentUserPhone}`}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        dividers 
        sx={{ 
          minHeight: isMobile ? 'auto' : 400, 
          bgcolor: 'grey.50',
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 2, sm: 3 }
        }}
      >
        {/* Filter UI */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, bgcolor: 'white' }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FilterListIcon color="primary" />
            <Typography variant={isSmallMobile ? "body1" : "h6"} color="primary">Filter Transactions</Typography>
          </Box>
          <Stack 
            direction={isSmallMobile ? "column" : "row"} 
            spacing={2} 
            alignItems={isSmallMobile ? "stretch" : "center"}
          >
            <TextField
              type="date"
              label="From Date"
              size="small"
              value={from}
              onChange={e => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 150 }}
            />
            <TextField
              type="date"
              label="To Date"
              size="small"
              value={to}
              onChange={e => setTo(e.target.value)}  
              InputLabelProps={{ shrink: true }}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 150 }}
            />
            <Button
              variant="contained"
              size="medium"
              onClick={fetchHistory}
              disabled={trx_history_loading}
              startIcon={<FilterListIcon />}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 120 }}
            >
              Filter
            </Button>
          </Stack>
        </Paper>

        {/* Content */}
        {trx_history_loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <Box textAlign="center">
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 2 }}>Loading transactions...</Typography>
            </Box>
          </Box>
        ) : trx_history_data?.length ? (
          <>
            {/* Summary Section */}
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, bgcolor: 'white' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AccountBalanceIcon color="primary" />
                <Typography variant={isSmallMobile ? "body1" : "h6"} color="primary">Transaction Summary</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: { xs: 2, sm: 3 }, 
                      bgcolor: 'success.light', 
                      color: 'success.contrastText',
                      borderColor: 'success.main',
                      borderWidth: 2
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <TrendingUpIcon />
                      <Typography variant={isSmallMobile ? "subtitle1" : "h6"} fontWeight={600}>Incoming</Typography>
                    </Box>
                    <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={700}>{summary.inCredit} Credits</Typography>
                    <Typography variant="body1">{summary.inBirr} Birr</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: { xs: 2, sm: 3 }, 
                      bgcolor: 'warning.light', 
                      color: 'warning.contrastText',
                      borderColor: 'warning.main',
                      borderWidth: 2
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <TrendingDownIcon />
                      <Typography variant={isSmallMobile ? "subtitle1" : "h6"} fontWeight={600}>Outgoing</Typography>
                    </Box>
                    <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={700}>{summary.outCredit} Credits</Typography>
                    <Typography variant="body1">{summary.outBirr} Birr</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>

            {/* Table Section - Desktop */}
            {!isMobile ? (
              <Paper elevation={2} sx={{ bgcolor: 'white' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SwapHorizIcon color="primary" />
                    <Typography variant="h6" color="primary">Transaction Details</Typography>
                    <Chip 
                      label={`${trx_history_data.length} transactions`} 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                    />
                  </Box>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Sender</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Receiver</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Date & Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trx_history_data.map((tx, index) => {
                        const isIncoming = isIncomingTransaction(tx);
                        return (
                          <TableRow 
                            key={index}
                            sx={{ 
                              '&:hover': { bgcolor: 'grey.50' },
                              borderLeft: `4px solid ${isIncoming ? '#4caf50' : '#ff9800'}`
                            }}
                          >
                            <TableCell>
                              {getTransactionTypeChip(tx)}
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {tx.senderName}
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <PhoneIcon fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      {tx.senderPhone}
                                    </Typography>
                                  </Box>
                                  <Chip 
                                    label={tx.senderRole} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {tx.receiverName}
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <PhoneIcon fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      {tx.receiverPhone}
                                    </Typography>
                                  </Box>
                                  <Chip 
                                    label={tx.receiverRole} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box>
                                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                  <CurrencyExchangeIcon fontSize="small" color="primary" />
                                  <Typography 
                                    variant="h6" 
                                    color="primary" 
                                    fontWeight={700}
                                  >
                                    {tx.amountTransferred?.toFixed(2)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Credits
                                  </Typography>
                                </Box>
                                <Typography 
                                  variant="body2" 
                                  color="secondary" 
                                  fontWeight={500}
                                >
                                  {tx.creditInBirr?.toFixed(2)} Birr
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                <ScheduleIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {new Date(tx.createdAt).toLocaleString()}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              /* Mobile Card View */
              <Box>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
                  <SwapHorizIcon color="primary" />
                  <Typography variant="h6" color="primary">Transaction Details</Typography>
                  <Chip 
                    label={`${trx_history_data.length}`} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                </Box>
                <Stack spacing={2}>
                  {trx_history_data.map((tx, index) => {
                    const isIncoming = isIncomingTransaction(tx);
                    return (
                      <Card 
                        key={index} 
                        elevation={2}
                        sx={{ 
                          borderLeft: `4px solid ${isIncoming ? '#4caf50' : '#ff9800'}`,
                          bgcolor: 'white'
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          {/* Transaction Type */}
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            {getTransactionTypeChip(tx)}
                            <Typography variant="caption" color="text.secondary">
                              {new Date(tx.createdAt).toLocaleString()}
                            </Typography>
                          </Box>

                          {/* Amount */}
                          <Box textAlign="center" mb={2} py={1} bgcolor="grey.50" borderRadius={1}>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                              <CurrencyExchangeIcon color="primary" />
                              <Typography variant="h5" color="primary" fontWeight={700}>
                                {tx.amountTransferred}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Credits
                              </Typography>
                            </Box>
                            <Typography variant="body1" color="secondary" fontWeight={500}>
                              {tx.creditInBirr} Birr
                            </Typography>
                          </Box>

                          {/* Sender & Receiver */}
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  FROM
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                                    <PersonIcon fontSize="small" />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {tx.senderName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {tx.senderPhone}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Chip 
                                  label={tx.senderRole} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  TO
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
                                    <PersonIcon fontSize="small" />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {tx.receiverName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {tx.receiverPhone}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Chip 
                                  label={tx.receiverRole} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </>
        ) : (
          <Paper elevation={2} sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center', bgcolor: 'white' }}>
            <SwapHorizIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'grey.400', mb: 2 }} />
            <Typography variant={isSmallMobile ? "body1" : "h6"} color="textSecondary" gutterBottom>
              No Transactions Found
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {trx_history_message || "No transactions found in the selected date range."}
            </Typography>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionHistoryModal;